import { useMe } from '@/hooks/queries/useMe'
import { shouldFilterByOutletType, shouldRestrictByAssignedGeography } from '@repo/permissions'
import { useEffect, useMemo, useRef } from 'react'
import { AuditTrailsSearch } from '../useAuditTrails'
import { OrderSearch } from '../useOrders'
import { PaymentReviewSearch } from '../usePaymentReview'
import { useOutletSubtypes } from '../useOutlets'

type UseQueryDefaultsOptions<T> = {
  search: T
  defaults: T
  navigate: (opts: { search: T }) => void
}

type AuditTrailsDefaults = Pick<AuditTrailsSearch, 'createdAt'>
type OrdersDefaults = Pick<OrderSearch, 'date' | 'outletSubtype' | 'status' | 'territory'>
type PaymentReviewDefaults = Pick<PaymentReviewSearch, 'ptrStatus'>

export function useDefaultTerritories() {
  const { data: user } = useMe()

  return useMemo(() => {
    if (!shouldRestrictByAssignedGeography(user?.roles ?? [])) {
      return []
    }

    return (
      user?.assigned.territories
        .filter((territory) => territory.isPrimary)
        .map((territory) => territory.name) ?? []
    )
  }, [user?.roles, user?.assigned.territories])
}

export function useDefaultOutletSubtypes() {
  const { data: user } = useMe()
  const shouldFilter = shouldFilterByOutletType(user?.roles ?? [])
  const { data: outletSubtypes } = useOutletSubtypes('flagged', { enabled: shouldFilter })

  return useMemo(() => {
    return shouldFilter
      ? (outletSubtypes?.results?.map((outletSubtype) => outletSubtype.sfId ?? '') ?? [])
      : []
  }, [shouldFilter, outletSubtypes?.results])
}

export function useQueryDefaults<
  T extends OrdersDefaults & AuditTrailsDefaults & PaymentReviewDefaults,
>({ search, defaults, navigate }: UseQueryDefaultsOptions<T>) {
  const defaultsApplied = useRef(false)

  useEffect(() => {
    if (defaultsApplied.current) return

    const updatedSearch = { ...search }
    let shouldNavigate = false

    if (!search.date && defaults.date) {
      updatedSearch.date = defaults.date
      shouldNavigate = true
    }

    if (!search.territory && defaults.territory && defaults.territory.length > 0) {
      updatedSearch.territory = defaults.territory
      shouldNavigate = true
    }

    if (!search.outletSubtype && defaults.outletSubtype && defaults.outletSubtype.length > 0) {
      updatedSearch.outletSubtype = defaults.outletSubtype
      shouldNavigate = true
    }

    if (!search.createdAt && defaults.createdAt) {
      updatedSearch.createdAt = defaults.createdAt
      shouldNavigate = true
    }

    if (!search.status && defaults.status) {
      updatedSearch.status = defaults.status
      shouldNavigate = true
    }

    if (!search.ptrStatus && defaults.ptrStatus) {
      updatedSearch.ptrStatus = defaults.ptrStatus
      shouldNavigate = true
    }

    if (shouldNavigate) {
      // Back to the first page: the defaults change which rows match, so the page number the
      // user arrived with almost certainly points past the end of the new result set.
      navigate({ search: { ...updatedSearch, page: 1 } })
      defaultsApplied.current = true
    }
  }, [
    search,
    defaults.date,
    defaults.territory,
    defaults.outletSubtype,
    defaults.createdAt,
    defaults.status,
    defaults.ptrStatus,
    navigate,
  ])
}
